import React, { useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRightFromBracket, faUser, faLock } from "@fortawesome/free-solid-svg-icons";
import { primaryColor, secondaryColor } from "../../utils/comon";

const ProfileDropdown = ({ isOpen, onClose, onLogout, onUpdatePassword, userData, displayName, displayRole, displayImage }) => {
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  // Use resolved props from Header (from localStorage), fall back to userData
  const fullName = displayName
    || (userData ? `${userData.firstName || ''} ${userData.lastName || ''}`.trim() : '')
    || 'Administrator';

  const roleName = displayRole
    || (userData?.roles?.length > 0
      ? userData.roles.map(r => r.roleName).join(', ')
      : userData?.role || '')
    || 'Admin';

  const profileImgSrc = displayImage || userData?.userImages?.[0]?.image;
  const hasValidProfileImage = profileImgSrc && profileImgSrc !== 'null' && profileImgSrc !== 'undefined' && profileImgSrc.trim() !== '';

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      ref={dropdownRef}
      className="absolute right-0 mt-2 w-64 bg-white dark:bg-cardbg rounded-2xl shadow-xl border border-gray-100 dark:border-bordercolor overflow-hidden z-50 animate-in fade-in zoom-in duration-200"
    >
      {/* User Info Header */}
      <div className="p-5 flex items-center gap-3 border-b border-gray-50 dark:border-bordercolor">
        <div className="relative w-10 h-10 rounded-xl bg-gray-100 overflow-hidden flex-shrink-0 border border-gray-100">
          <img
            src={`https://ui-avatars.com/api/?name=${encodeURIComponent(fullName)}&background=${secondaryColor()?.replace('#', '')}&color=${primaryColor()?.replace('#', '')}&bold=true`}
            alt="Avatar"
            className="absolute inset-0 w-full h-full object-cover"
          />
          {hasValidProfileImage && (
            <img
              src={profileImgSrc}
              alt="Avatar"
              className="absolute inset-0 w-full h-full object-cover z-10"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          )}
        </div>
        <div className="flex flex-col overflow-hidden">
          <span className="text-sm font-bold text-[#0F172A] dark:text-textprimary truncate">{fullName}</span>
          <span className="text-[11px] text-[#94A3B8] font-medium truncate uppercase tracking-wider">{roleName}</span>
        </div>
      </div>

      {/* Profile Actions */}
      <div className="p-2 border-t border-gray-50 dark:border-bordercolor">
        <button
          onClick={() => { navigate("/zentcomm/my-profile"); onClose(); }}
          className="w-full flex items-center gap-3 px-3 py-3 rounded-xl transition-colors group text-left mb-1"
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = "var(--color-secondaryaccent)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "transparent";
          }}
        >
          <div className="w-6 h-6 rounded-lg flex items-center justify-center text-blue-500 bg-blue-50 group-hover:bg-blue-100 transition-colors"
            style={{ color: "var(--color-primaryaccent)", backgroundColor: "var(--color-secondaryaccent)" }}>
            <FontAwesomeIcon icon={faUser} className="text-xs" />
          </div>
          <span className="text-sm font-normal text-gray-700 group-hover:text-blue-600 transition-colors"
            style={{ color: "var(--color-primaryaccent)" }}>
            My Profile
          </span>
        </button>

        <button
          onClick={() => { onUpdatePassword(); onClose(); }}
          className="w-full flex items-center gap-3 px-3 py-3 rounded-xl transition-colors group text-left mb-1"
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = "var(--color-secondaryaccent)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "transparent";
          }}
        >
          <div className="w-6 h-6 rounded-lg flex items-center justify-center text-blue-500 bg-blue-50 group-hover:bg-blue-100 transition-colors"
            style={{ color: "var(--color-primaryaccent)", backgroundColor: "var(--color-secondaryaccent)" }}>
            <FontAwesomeIcon icon={faLock} className="text-xs" />
          </div>
          <span className="text-sm font-normal text-gray-700 group-hover:text-blue-600 transition-colors"
            style={{ color: "var(--color-primaryaccent)" }}>
            Update Password
          </span>
        </button>

        <button
          onClick={() => { onLogout(); onClose(); }}
          className="w-full flex items-center gap-3 px-3 py-3 rounded-xl transition-colors group text-left"
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = "var(--color-secondaryaccent)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "transparent";
          }}
        >
          <div className="w-6 h-6 rounded-lg flex items-center justify-center text-red-500 bg-red-50 group-hover:bg-red-100 transition-colors"
            style={{ color: "var(--color-primaryaccent)", backgroundColor: "var(--color-secondaryaccent)" }}>
            <FontAwesomeIcon icon={faRightFromBracket} className="text-xs" />
          </div>
          <span className="text-sm font-normal text-red-500 group-hover:text-red-600 transition-colors"
            style={{ color: "var(--color-primaryaccent)" }}>
            Logout
          </span>
        </button>
      </div>
    </div>
  );
};

export default ProfileDropdown;
